import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuthUser } from '../../hooks/useAuthUser';
import { UserContext } from '../../contexts/UserContext';
import style from '../GeneratedPlanPage/GeneratedPlanPage.module.css';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import AppLoader from '../../components/AppLoader/AppLoader';
import {
	MdCalendarMonth,
	MdExpandMore,
	MdExpandLess,
	MdFitnessCenter,
	MdSelfImprovement,
	MdPlayArrow,
} from 'react-icons/md';
import WorkoutSession from '../../components/WorkoutSession/WorkoutSession';
import { toast } from 'react-toastify';
import { requestWithApiFallback } from '../../utils/apiRequest';

const API_URL = process.env.REACT_APP_API_URL;

const SavedPlanPage = () => {
	const { user, isLoaded } = useAuthUser();
	const { dbUser, setDbUser, isDbUserResolved } = useContext(UserContext);
	const navigate = useNavigate();
	const { planId } = useParams();
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), []);

	const [plan, setPlan] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadErrorType, setLoadErrorType] = useState('');
	const [expandedDays, setExpandedDays] = useState({});
	const [activeWeek, setActiveWeek] = useState(1);
	const [deleting, setDeleting] = useState(false);
	const [activeSessionDay, setActiveSessionDay] = useState(null);
	const [completionSummary, setCompletionSummary] = useState(null);
	const [showCompletionBanner, setShowCompletionBanner] = useState(false);
	const [reloadTick, setReloadTick] = useState(0);
	const activeSessionStorageKey = useMemo(
		() => (planId ? `saved-plan:active-session:${planId}` : ''),
		[planId]
	);

	const planDuration = useMemo(() => plan?.planDuration || (plan?.days?.length ?? 0), [plan]);
	const localPlan = useMemo(
		() => (dbUser?.workoutPlans || []).find((savedPlan) => String(savedPlan?._id) === String(planId)) ?? null,
		[dbUser?.workoutPlans, planId]
	);

	const totalWorkoutDays = useMemo(
		() => plan?.days?.filter((d) => !d.isRestDay).length ?? 0,
		[plan]
	);
	const totalRestDays = useMemo(
		() => plan?.days?.filter((d) => d.isRestDay).length ?? 0,
		[plan]
	);
	const weeks = useMemo(() => {
		if (!plan?.days) return [];
		const w = [];
		for (let i = 0; i < plan.days.length; i += 7) {
			w.push(plan.days.slice(i, i + 7));
		}
		return w;
	}, [plan]);

	const toggleDay = (dayNum) => {
		setExpandedDays((prev) => ({ ...prev, [dayNum]: !prev[dayNum] }));
	};

	const getCachedPlan = useCallback(() => {
		if (!planId) return null;
		try {
			const raw = window.localStorage.getItem(`saved-plan:${planId}`);
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			if (!parsed?.days?.length) return null;
			return parsed;
		} catch {
			return null;
		}
	}, [planId]);

	const cachePlan = useCallback((nextPlan) => {
		if (!planId || !nextPlan?.days?.length) return;
		try {
			window.localStorage.setItem(`saved-plan:${planId}`, JSON.stringify(nextPlan));
		} catch {}
	}, [planId]);

	useEffect(() => {
		if (!activeSessionStorageKey) return;
		try {
			if (activeSessionDay?.workouts?.length) {
				window.localStorage.setItem(activeSessionStorageKey, JSON.stringify(activeSessionDay));
			} else {
				window.localStorage.removeItem(activeSessionStorageKey);
			}
		} catch {}
	}, [activeSessionDay, activeSessionStorageKey]);

	useEffect(() => {
		if (!activeSessionStorageKey || activeSessionDay?.workouts?.length) return;
		try {
			const raw = window.localStorage.getItem(activeSessionStorageKey);
			if (!raw) return;
			const parsedSessionDay = JSON.parse(raw);
			if (parsedSessionDay?.workouts?.length) {
				setActiveSessionDay(parsedSessionDay);
			}
		} catch {}
	}, [activeSessionDay?.workouts?.length, activeSessionStorageKey]);

	useEffect(() => {
		if (!showCompletionBanner) return;
		const timeoutId = setTimeout(() => setShowCompletionBanner(false), 4000);
		return () => clearTimeout(timeoutId);
	}, [showCompletionBanner]);

	useEffect(() => {
		if (!planId) {
			setPlan(null);
			setLoadErrorType('not-found');
			setLoading(false);
			return;
		}

		if (!isDbUserResolved) {
			setLoading(true);
			return;
		}

		if (localPlan?.days?.length) {
			setPlan(localPlan);
			setLoadErrorType('');
			cachePlan(localPlan);
			setLoading(false);
			return;
		}

		const cachedPlan = getCachedPlan();
		if (cachedPlan?.days?.length) {
			setPlan(cachedPlan);
		}

		if (!isLoaded) {
			setLoading(true);
			return;
		}

		if (!user?.id) {
			setPlan(null);
			setLoadErrorType('temporary');
			setLoading(false);
			return;
		}

		let cancelled = false;
		setLoading(true);
		(async () => {
			try {
				let response = null;
				let lastError = null;
				const retryDelays = [0, 400, 900];
				for (let i = 0; i < retryDelays.length; i += 1) {
					if (retryDelays[i] > 0) {
						await new Promise((resolve) => setTimeout(resolve, retryDelays[i]));
					}
					try {
						response = await requestApi((base) => axios.get(`${base}/users/${user.id}/workout-plans/${planId}`));
						lastError = null;
						break;
					} catch (err) {
						lastError = err;
						if (err?.response?.status === 404) break;
					}
				}

				if (cancelled) return;

				if (response?.data?.days?.length) {
					setPlan(response.data);
					setLoadErrorType('');
					cachePlan(response.data);
					return;
				}

				if (lastError?.response?.status === 404) {
					setLoadErrorType('not-found');
					setPlan(cachedPlan?.days?.length ? cachedPlan : null);
					return;
				}

				setLoadErrorType('temporary');
				if (!cachedPlan?.days?.length) {
					setPlan(null);
				}
				toast.error('Could not refresh your plan. Showing last available data.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [cachePlan, getCachedPlan, isDbUserResolved, isLoaded, localPlan, planId, reloadTick, requestApi, user?.id]);

	const handleBackToProfile = () => {
		navigate('/profile-page', { state: { activeTab: 'plans' } });
	};

	const handleDeletePlan = async () => {
		if (!user?.id || !planId || deleting) return;
		if (!window.confirm('Delete this plan? This cannot be undone.')) return;
		setDeleting(true);
		try {
			await requestApi((base) =>
				axios.delete(`${base}/users/${user.id}/workout-plans/${planId}`)
			);
			setDbUser((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					workoutPlans: (prev.workoutPlans || []).filter(
						(savedPlan) => String(savedPlan?._id) !== String(planId)
					),
				};
			});
			toast.success('Plan deleted.');
			navigate('/profile-page', { state: { activeTab: 'plans' } });
		} catch (err) {
			console.error(err);
			toast.error('Could not delete plan.');
		} finally {
			setDeleting(false);
		}
	};

	const handleSessionComplete = (summary) => {
		setCompletionSummary(summary);
		setShowCompletionBanner(true);
	};

	const hasActiveSession = Boolean(activeSessionDay?.workouts?.length);

	const handleShareCompletionToFeed = async (sharePayload) => {
		if (!user?.id || !completionSummary) return;
		try {
			await requestApi((base) =>
				axios.post(
					`${base}/stryds/share-completion/${user.id}/${encodeURIComponent(completionSummary.workoutName || plan?.planName || 'workout-session')}`,
					{
						xpEarned: completionSummary.xpEarned || 0,
						routineCreatorID: user.id,
						shareText: sharePayload?.shareText,
						shareLink: sharePayload?.shareLink,
					}
				)
			);
			toast.success('Shared to feed!');
		} catch (err) {
			console.error(err);
			toast.error('Could not share to feed right now.');
		}
	};

	if ((!isLoaded || !user) && !hasActiveSession) {
		return (
			<div className={style.GeneratedPlanPage}>
				<div className={style.ErrorState}>
					<p>Please log in to view your plan.</p>
					<PrimaryButton text="Go to profile" click={() => navigate('/profile-page')} />
				</div>
			</div>
		);
	}

	if (loading && !hasActiveSession) {
		return (
			<div className={style.GeneratedPlanPage}>
				<AppLoader message="Loading plan..." />
			</div>
		);
	}

	if ((!plan || !plan.days?.length) && !hasActiveSession) {
		return (
			<div className={style.GeneratedPlanPage}>
				<div className={style.ErrorState}>
					{loadErrorType === 'not-found' ? (
						<>
							<h2>Plan not found</h2>
							<p>This plan may have been deleted or the link is invalid.</p>
							<PrimaryButton text="Back to profile" click={handleBackToProfile} />
						</>
					) : (
						<>
							<h2>Unable to load plan</h2>
							<p>We hit a temporary issue while loading your plan.</p>
							<PrimaryButton text="Retry" click={() => setReloadTick((tick) => tick + 1)} />
							<SecondaryButton text="Back to profile" click={handleBackToProfile} />
						</>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={style.GeneratedPlanPage}>
			{showCompletionBanner && completionSummary && (
				<div className={style.CompletionBanner}>
					<span className={style.CompletionBannerEmoji}>🎉</span>
					<span>
						Workout complete: <strong>{completionSummary.workoutName}</strong> (+{completionSummary.xpEarned} XP)
					</span>
				</div>
			)}

			{plan?.days?.length ? (
				<>
					<div className={style.PlanHeader}>
						<div className={style.PlanBadge}>
							<MdCalendarMonth />
							<span>{planDuration}-Day Plan</span>
						</div>
						<h1 className={style.PlanName}>{plan.planName}</h1>
						{plan.planDescription && (
							<p className={style.PlanDescription}>{plan.planDescription}</p>
						)}

						<div className={style.PlanStats}>
							<div className={style.Stat}>
								<MdFitnessCenter className={style.StatIcon} />
								<div className={style.StatContent}>
									<span className={style.StatValue}>{totalWorkoutDays}</span>
									<span className={style.StatLabel}>Workout Days</span>
								</div>
							</div>
							<div className={style.StatDivider} />
							<div className={style.Stat}>
								<MdSelfImprovement className={style.StatIcon} />
								<div className={style.StatContent}>
									<span className={style.StatValue}>{totalRestDays}</span>
									<span className={style.StatLabel}>Rest Days</span>
								</div>
							</div>
							<div className={style.StatDivider} />
							<div className={style.Stat}>
								<MdCalendarMonth className={style.StatIcon} />
								<div className={style.StatContent}>
									<span className={style.StatValue}>{planDuration}</span>
									<span className={style.StatLabel}>Total Days</span>
								</div>
							</div>
						</div>
					</div>

					{weeks.length > 1 && (
						<div className={style.WeekTabs}>
							{weeks.map((_, i) => (
								<button
									key={i}
									className={`${style.WeekTab} ${activeWeek === i + 1 ? style.WeekTabActive : ''}`}
									onClick={() => setActiveWeek(i + 1)}
								>
									Week {i + 1}
								</button>
							))}
						</div>
					)}

					<div className={style.DaysList}>
						{(weeks[activeWeek - 1] || []).map((day) => (
							<div
								key={day.day}
								className={`${style.DayCard} ${day.isRestDay ? style.RestDay : ''} ${expandedDays[day.day] ? style.DayExpanded : ''}`}
							>
								<div className={style.DayHeader}>
									<button className={style.DayHeaderToggle} onClick={() => !day.isRestDay && toggleDay(day.day)}>
										<div className={style.DayNumber}>
											<span className={style.DayNum}>{day.day}</span>
										</div>
										<div className={style.DayInfo}>
											<span className={style.DayName}>{day.dayName}</span>
											{day.isRestDay ? (
												<span className={style.RestLabel}>{day.restTip || 'Recovery & rest'}</span>
											) : (
												<span className={style.DayMeta}>
													{day.focusArea} · {day.estimatedTimeMinutes} min
												</span>
											)}
										</div>
									</button>
									<div className={style.DayActions}>
										{!day.isRestDay && day.workouts?.length > 0 && (
											<button
												type='button'
												className={style.DayPlayBtn}
												aria-label={`Start ${day.dayName}`}
												onClick={() => setActiveSessionDay(day)}
											>
												<MdPlayArrow />
											</button>
										)}
										{day.isRestDay ? (
											<div className={style.RestIcon}>
												<MdSelfImprovement />
											</div>
										) : (
											<button type='button' className={style.ExpandIcon} onClick={() => toggleDay(day.day)}>
												{expandedDays[day.day] ? <MdExpandLess /> : <MdExpandMore />}
											</button>
										)}
									</div>
								</div>
								{!day.isRestDay && expandedDays[day.day] && (
									<div className={style.DayWorkouts}>
										{(day.workouts || []).map((w, idx) => (
											<div key={idx} className={style.WorkoutRow}>
												<span className={style.WorkoutIndex}>{idx + 1}</span>
												<div className={style.WorkoutInfo}>
													<span className={style.WorkoutName}>{w.workoutType}</span>
													<span className={style.WorkoutDetail}>
														{w.workoutSets} · {w.workoutReps}
													</span>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						))}
					</div>

					<div className={style.Actions}>
						<SecondaryButton text="Back to profile" click={handleBackToProfile} />
						<SecondaryButton
							text={deleting ? 'Deleting...' : 'Delete plan'}
							click={handleDeletePlan}
							disabled={deleting}
						/>
					</div>
				</>
			) : (
				<div className={style.ErrorState}>
					<AppLoader message="Restoring workout session..." />
				</div>
			)}

			{activeSessionDay && activeSessionDay.workouts?.length > 0 && (
				<WorkoutSession
					workouts={activeSessionDay.workouts}
					routineName={activeSessionDay.dayName || plan?.planName || 'Workout Session'}
					onEnd={() => {
						setActiveSessionDay(null);
						if (activeSessionStorageKey) {
							try {
								window.localStorage.removeItem(activeSessionStorageKey);
							} catch {}
						}
					}}
					onSessionComplete={handleSessionComplete}
					onShareCompletion={handleShareCompletionToFeed}
					shareLink={`${window.location.origin}/plan/${planId}`}
				/>
			)}
		</div>
	);
};

export default SavedPlanPage;
